import { useGetAllShopEmailTemplates } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import useGenerateDefaultReturnTemplates from "@babackapp/private-api-client/client/use-generate-default-return-templates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { Separator } from "@babackapp/ui/components/separator";
import { Switch } from "@babackapp/ui/components/switch";
import { Label } from "@babackapp/ui/components/label";
import { Button } from "@babackapp/ui/components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/ui/components/tooltip";
import { Info, Pencil, Wand2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useTranslation from "@babackapp/translations/use-translation";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team";

import type { ShopPreferencesNotificationsReturnEmailsDto } from "@babackapp/private-api-client/types";

const RETURN_EMAIL_TYPE_ENTRIES = [
  { field: "returnApproved",                    labelSuffix: "return-approved" },
  { field: "returnRejected",                    labelSuffix: "return-rejected" },
  { field: "returnLabelCreated",                labelSuffix: "return-label-created" },
  { field: "returnCreatedWithoutApproval",      labelSuffix: "return-created-without-approval" },
  { field: "returnCreatedWithoutLabel",         labelSuffix: "return-created-without-label" },
  { field: "returnCreatedWithoutPhysicalReturn",labelSuffix: "return-created-without-physical-return" },
  { field: "returnCreatedReturnToStore",        labelSuffix: "return-created-return-to-store" },
  { field: "returnDepositReminder",             labelSuffix: "return-deposit-reminder" },
  { field: "returnDiscountCreated",             labelSuffix: "return-discount-created" },
] as const satisfies {
  field: keyof ShopPreferencesNotificationsReturnEmailsDto;
  labelSuffix: string;
}[];

export type ReturnEmailConfig = {
  isEnabled: boolean;
  templateId?: string | null;
};

export type ReturnEmailConfigs = Partial<ShopPreferencesNotificationsReturnEmailsDto>;

type Props = {
  value: ReturnEmailConfigs;
  onChange: (value: ReturnEmailConfigs) => void;
  /**
   * Sync the configs in the form WITHOUT persisting them. Used after the
   * superadmin "generate defaults" action, which has already saved them
   * server-side — calling `onChange` here would trigger a redundant PATCH.
   */
  onGenerated?: (value: ReturnEmailConfigs) => void;
};

export function ReturnEmailTemplatesSettings({
  value,
  onChange,
  onGenerated,
}: Props) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { data: shop } = useCurrentShopSuspense();
  const { data: returnTemplates = [] } = useGetAllShopEmailTemplates(
    shop.id,
    "return",
  );

  const isSuperAdmin = useViewerIsSuperAdmin();
  const generateDefaults = useGenerateDefaultReturnTemplates(shop.id);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);

  const handleGenerateConfirm = () => {
    generateDefaults.mutate(undefined, {
      // Sync the form so the dropdowns reflect the newly created + linked
      // templates. The backend already persisted them, so update local state
      // only (no PATCH); queries are also invalidated by the hook.
      onSuccess: (data) => onGenerated?.(data.returnEmailConfigs),
    });
    setIsGenerateDialogOpen(false);
  };

  const getConfig = (field: keyof ShopPreferencesNotificationsReturnEmailsDto): ReturnEmailConfig =>
    (value[field] as ReturnEmailConfig | undefined) ?? { isEnabled: true, templateId: null };

  const update = (
    field: keyof ShopPreferencesNotificationsReturnEmailsDto,
    patch: Partial<ReturnEmailConfig>,
  ) => {
    onChange({
      ...value,
      [field]: { ...getConfig(field), ...patch },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="text-sm font-semibold">
          {t("settings.customerPortal.notifications.returnEmailTemplates.title")}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t(
            "settings.customerPortal.notifications.returnEmailTemplates.description",
          )}
        </p>
      </div>

      <div className="flex flex-col gap-4">
        {RETURN_EMAIL_TYPE_ENTRIES.map(({ field, labelSuffix }) => {
          const config = getConfig(field);
          const labelKey = `settings.customerPortal.notifications.returnEmailTypes.${labelSuffix}`;
          const hintKey = `settings.customerPortal.notifications.returnEmailTypeHints.${labelSuffix}`;
          return (
            <div key={field} className="flex flex-col gap-3 p-4 border rounded-lg">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5">
                  <Label className="text-sm font-medium">{t(labelKey)}</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={t(hintKey)}
                        className="text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {t(hintKey)}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Switch
                  checked={config.isEnabled}
                  onCheckedChange={(checked) =>
                    update(field, { isEnabled: checked })
                  }
                />
              </div>

              {config.isEnabled && (
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select
                      value={config.templateId ?? "default"}
                      onValueChange={(v) =>
                        update(field, {
                          templateId: v === "default" ? null : v,
                        })
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t(
                            "settings.customerPortal.notifications.returnEmailTemplates.defaultPlaceholder",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">
                          {t(
                            "settings.customerPortal.notifications.returnEmailTemplates.defaultOption",
                          )}
                        </SelectItem>
                        {returnTemplates.map((tmpl) => (
                          <SelectItem key={tmpl.id} value={tmpl.id}>
                            {tmpl.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    disabled={!config.templateId}
                    title={t(
                      "settings.customerPortal.notifications.returnEmailTemplates.editTemplate",
                    )}
                    onClick={() =>
                      config.templateId &&
                      navigate(
                        `/shops/${shop.id}/settings/notifications/email-templates/${config.templateId}`,
                      )
                    }
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isSuperAdmin && (
        <div className="border-t pt-4 flex flex-col gap-2 items-start">
          <Button
            type="button"
            variant="outline"
            disabled={generateDefaults.isPending}
            onClick={() => setIsGenerateDialogOpen(true)}
          >
            <Wand2 className="h-4 w-4 mr-2" />
            {t(
              "settings.customerPortal.notifications.returnEmailTemplates.generateDefaults.button",
            )}
          </Button>
          <p className="text-xs text-muted-foreground">
            {t(
              "settings.customerPortal.notifications.returnEmailTemplates.generateDefaults.hint",
            )}
          </p>
        </div>
      )}

      <AlertDialog
        open={isGenerateDialogOpen}
        onOpenChange={setIsGenerateDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "settings.customerPortal.notifications.returnEmailTemplates.generateDefaults.dialogTitle",
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "settings.customerPortal.notifications.returnEmailTemplates.generateDefaults.dialogDescription",
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(
                "settings.customerPortal.notifications.returnEmailTemplates.generateDefaults.cancel",
              )}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleGenerateConfirm}>
              {t(
                "settings.customerPortal.notifications.returnEmailTemplates.generateDefaults.confirm",
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
